import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import 'yup-phone';

import TextInput from '../TextInput';
import LockCopy from '../LockCopy';

import { purchaseFlowOperations } from '@evdy/web-redux/dist/lib/webPurchase/purchaseFlow';
import { authUserOperations } from '@evdy/web-redux/dist/lib/authUser';

import checkIcon from '../../public/images/icons/dark-check-icon.svg';

import styles from './PhoneForm.module.scss';

const { updateWebleadData, resetWebleadUpdated } = authUserOperations;

const {
  'mobile-phone-form': mobilePhoneFormClass,
  'password-copy': passwordCopyClass,
  'phone-input-container': phoneInputContainerClass,
  'phone-input': phoneInputClass
} = styles;

const MobilePhoneForm = ({ moveToNextStep }) => {
  const dispatch = useDispatch();

  const { authUser = {}, purchaseFlow = {} } = useSelector(({ authUser, webPurchase }) => ({
    authUser,
    purchaseFlow: webPurchase.purchaseFlow
  }));

  // assign most prioritized user data
  const { data: userData, webleadUpdated } = useMemo(() => {
    return !!authUser?.data?._id ? authUser : purchaseFlow;
  }, [authUser, purchaseFlow]);

  const { _id: userId, phone, meta = {} } = userData ?? {};
  const userPhone = phone || meta?.webLeadPhone || '';

  // after submit, navigate to next step
  useEffect(() => {
    if (webleadUpdated) {
      dispatch(purchaseFlowOperations?.resetUpdateWeblead());
      dispatch(resetWebleadUpdated());
      moveToNextStep();
    }
  }, [dispatch, moveToNextStep, webleadUpdated]);

  const MobilePhoneFormSchema = Yup.object().shape({
    phone: Yup.string()
      .phone('US', true, 'Please enter a valid phone number.') // via yup-phone
      .required('This is required!')
  });

  const handleSubmit = async e => {
    const strippedPhone = e?.phone?.toString()?.replace(/\D+/g, '');
    const updatedUserBody = {
      meta: {
        webLeadPhone: strippedPhone
      },
      eFuneral: {
        lastScreen: {
          flow: 'everdays',
          screen: 'phoneForm',
          date: new Date().toISOString()
        }
      }
    };
    if (authUser?.data?._id) {
      dispatch(updateWebleadData(updatedUserBody));
    } else {
      dispatch(purchaseFlowOperations.updateWeblead(userId, updatedUserBody));
    }

    moveToNextStep();
  };

  const initialValues = { phone: userPhone || '' };

  return (
    <div className={mobilePhoneFormClass}>
      <h3>Set up your account to see pricing options</h3>
      <Formik
        initialValues={initialValues}
        validationSchema={MobilePhoneFormSchema}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnMount
      >
        {({ dirty, isValid }) => (
          <Form>
            <div className={phoneInputContainerClass}>
              <TextInput
                autoFocus
                name="phone"
                type="tel"
                label="Phone Number"
                placeholder="###-###-####"
                customClass={phoneInputClass}
              />
            </div>
            <div className={passwordCopyClass}>
              <img src={checkIcon} alt="check icon" />
              <span>You’ll never need a password to get back to with super quick login.</span>
            </div>
            <button disabled={!isValid} type="submit">
              Continue to my prices
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default MobilePhoneForm;
